import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { ArrowForwardIos, Search } from '@mui/icons-material';
import { Box, Grid, InputAdornment, OutlinedInput, Pagination, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { FilterPanelContainer, ItemListContainer, PageContainer } from 'components/GlobalStyles/styles';
import GridItem from 'components/GridItem';
import Loader from 'components/Loader';
import { useGetClientsQuery } from 'services/busmanApi';
import { sortLists } from 'utils/sort';

import { RootState } from '../../store';

import { Client, ClientsList } from './styles';

const ClientsPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isFetching } = useGetClientsQuery({});
  const { clients } = useSelector((state: RootState) => state.worklogs);
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { filterPanel } = useSelector((state: RootState) => state.filterPanel);

  const [inputText, setInputText] = React.useState('');
  const [currentPage, setCurrentPage] = React.useState(1);
  const [itemsPerPage] = React.useState(20);

  const handleClientClick = (clientID: string) => {
    navigate(`/clients/${clientID}`);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const filteredClients = sortLists([...clients], 'client_name').filter((data) => {
    if (inputText === '') {
      return true;
    }

    return data?.client_name?.toLowerCase().includes(inputText.toLowerCase());
  });

  React.useEffect(() => {
    setCurrentPage(1);
  }, [inputText]);

  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedClients = filteredClients.slice(startIndex, endIndex);

  return (
    <>
      <Helmet>
        <title>BusMin</title>
        <meta name="Busmin" content="EEA-Clients" />
      </Helmet>
      <Loader open={isFetching} />
      <PageContainer>
        {filterPanel && (
          <FilterPanelContainer>
            {!isMobileScreen && (
              <Typography gutterBottom sx={{ mb: theme.spacing(2) }} variant="h4">
                Clients
              </Typography>
            )}
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
              <OutlinedInput
                endAdornment={
                  <InputAdornment position="end">
                    <Search />
                  </InputAdornment>
                }
                onChange={(event) => setInputText(event.target.value)}
                placeholder="Search Client"
                sx={{
                  height: '32px',
                  flex: 1,
                  minWidth: '200px',
                }}
                value={inputText}
              />
            </Box>
          </FilterPanelContainer>
        )}
        <ItemListContainer>
          <Grid container display="flex" flexDirection="row">
            <GridItem title="CLIENT" seenOrIsHeading={true} breakpoint={2.5} />
          </Grid>

          <ClientsList>
            {paginatedClients.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  {inputText ? 'No clients found matching your search.' : 'No clients available.'}
                </Typography>
              </Box>
            ) : (
              paginatedClients.map((client) => {
                return (
                  <Client key={client.client_id} onClick={() => handleClientClick(client.client_id)}>
                    <GridItem title={client.client_name} seenOrIsHeading={false} breakpoint={2.5} />
                    <Grid
                      alignItems="center"
                      display="flex"
                      flexDirection="row"
                      item
                      justifyContent="end"
                      md={2}
                      sm={4}
                      xs={2}>
                      <ArrowForwardIos
                        sx={{ color: theme.palette.text.disabled, fontSize: '0.8rem', marginRight: theme.spacing(2) }}
                      />
                    </Grid>
                  </Client>
                );
              })
            )}
            {totalPages > 1 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  p: 2,
                  gap: 2,
                }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size={isMobileScreen ? 'small' : 'medium'}
                  siblingCount={isMobileScreen ? 0 : 1}
                  boundaryCount={1}
                />
              </Box>
            )}
          </ClientsList>
        </ItemListContainer>
      </PageContainer>
    </>
  );
};

export default ClientsPage;
